import { useState, useEffect, FC, HTMLAttributes, useRef, CSSProperties } from 'react';

import { motion } from 'framer-motion';
import styled, { keyframes } from 'styled-components';

import { theme } from '@styles/theme';
import { scrollIntoView } from '@utils/browser';
import { pxToRem } from '@utils/converters';
import { Timeout } from '@utils/types';

const FOCUS_DURATION_MS = 2500;

export type FocusBoxProps = HTMLAttributes<HTMLDivElement> & {
  isFocused: boolean;
  duration?: number;
  padding?: number;
};

export const FocusBox: FC<FocusBoxProps> = ({
  isFocused,
  duration = FOCUS_DURATION_MS,
  children,
  padding = 2,
  ...rest
}) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);
  const timeoutRef = useRef<Timeout>();
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isFocused) {
      clearTimeout(timeoutRef.current);
      setAnimationKey((prev) => prev + 1); // Increment key to force remount
      setIsAnimating(true);
      if (elementRef.current) {
        scrollIntoView(elementRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        setIsAnimating(false);
      }, FOCUS_DURATION_MS);

      return () => {
        clearTimeout(timeoutRef.current);
      };
    }
  }, [isFocused]);

  return (
    <Relative
      ref={elementRef}
      {...rest}
      style={
        {
          ...rest.style,
          '--focus-duration': duration + 'ms',
        } as CSSProperties
      }
    >
      {isAnimating && <StyledFocusBox key={animationKey} $padding={padding} />}
      {children}
    </Relative>
  );
};

const Relative = styled.div({
  position: 'relative',
});

const focusPulse = keyframes`
  0% {
    background-color: transparent;
    border-color: transparent;
  }
  50% {
    background-color: ${theme.color.indigoTrans5};
    border-color: ${theme.color.elementIndigo100};
  }
  100% {
    background-color: transparent;
    border-color: transparent;
  }
`;

const StyledFocusBox = styled(motion.div)<{ $padding: number }>`
  position: absolute;
  left: ${({ $padding }) => pxToRem(-$padding * 2)};
  top: ${({ $padding }) => pxToRem(-$padding)};
  bottom: ${({ $padding }) => pxToRem(-$padding)};
  right: ${({ $padding }) => pxToRem(-$padding * 2)};
  border: ${pxToRem(1)} solid transparent;
  background-color: transparent;
  border-radius: ${pxToRem(4)};
  animation-iteration-count: 1;
  animation: ${focusPulse} var(--focus-duration) ease-in-out;
  z-index: -1;
`;
